/**
 * Base adapter interface for converting between different task formats
 * Supports bidirectional conversion between Gongfu and Workflow formats
 */

export interface TaskMetadata {
  source: 'gongfu' | 'workflow';
  format: 'yaml' | 'json';
  version: string;
  created?: Date;
  updated?: Date;
}

export interface UnifiedTask {
  // Core fields
  id: string;
  title: string;
  description?: string;
  status: 'todo' | 'in_progress' | 'review' | 'done';
  priority: 'low' | 'normal' | 'high' | 'urgent';
  
  // Extended fields
  assignee?: string;
  tags?: string[];
  estimate?: string; // "3d", "2h", etc.
  dependencies?: string[];
  
  // Workflow-specific fields
  complexity?: {
    score: number;
    dimensions: Record<string, number>;
  };
  workflowId?: string;
  phase?: string;
  
  // Gongfu-specific fields
  type?: 'feature' | 'bug' | 'test' | 'doc' | 'refactor';
  owner?: string;
  
  // Metadata
  metadata: TaskMetadata;
  
  // Timestamps
  created: Date;
  updated: Date;
  completed?: Date;
}

export abstract class BaseAdapter<TSource, TTarget = UnifiedTask> {
  protected abstract readonly sourceFormat: string;
  protected abstract readonly targetFormat: string;
  
  /**
   * Convert from source format to unified format
   */
  abstract toUnified(source: TSource): TTarget;
  
  /**
   * Convert from unified format to source format
   */
  abstract fromUnified(unified: TTarget): TSource;
  
  /**
   * Batch convert multiple items
   */
  toUnifiedBatch(sources: TSource[]): TTarget[] {
    return sources.map(source => this.toUnified(source));
  }
  
  fromUnifiedBatch(unified: TTarget[]): TSource[] {
    return unified.map(item => this.fromUnified(item));
  }
  
  /**
   * Validate source data before conversion
   */
  protected validateSource(source: TSource): boolean {
    return source !== null && source !== undefined;
  }
  
  /**
   * Validate unified data before conversion
   */
  protected validateUnified(unified: TTarget): boolean {
    return unified !== null && unified !== undefined;
  }
  
  /**
   * Handle conversion errors
   */
  protected handleError(error: Error, context: string): never {
    throw new Error(`Adapter error in ${context}: ${error.message}`);
  }
}

/**
 * Factory for creating adapters
 */
export class AdapterFactory {
  private static adapters = new Map<string, BaseAdapter<any, any>>();
  
  static register(name: string, adapter: BaseAdapter<any, any>): void {
    this.adapters.set(name, adapter);
  }
  
  static get<T extends BaseAdapter<any, any>>(name: string): T {
    const adapter = this.adapters.get(name);
    if (!adapter) {
      throw new Error(`Adapter '${name}' not found`);
    }
    return adapter as T;
  }
  
  static list(): string[] {
    return Array.from(this.adapters.keys());
  }
}